/**
 * The Records page's filters as request parameters — one reading for the list
 * and for the semantic search (#1234).
 *
 * The semantic search used to receive only its query: the page showed a
 * selected status and project over matches from outside both. It now sends
 * these same parameters, and the server narrows its matches with the list's
 * own query.
 */
import type {
  RecordCriterionCondition,
  RecordOutcomeFilter,
  SemanticSearchFilterName,
} from '@aica/api-types';
import { formatRecordCriterionCondition } from '@aica/api-types/schemas';

export interface RecordsServerFilters {
  /** @deprecated pass `projectIds` — ignored whenever it is present and non-empty. */
  projectId?: string;
  /** Multi-project filter (project-multiselect K7). Empty/omitted = all projects. */
  projectIds?: string[];
  status?: string;
  /** Исход воронки «Головне» (#1108). */
  outcome?: RecordOutcomeFilter;
  type?: 'call' | 'chat' | 'email';
  crmEntityType?: 'deal' | 'lead' | 'contact' | 'company';
  /**
   * Номер сущности CRM (#743) — вкладка Aelo в карточке Bitrix24; только с
   * `crmEntityType` и только во встроенной сессии (вне неё сервер отвечает 400).
   */
  crmEntityId?: string;
  /**
   * Вкладка сделки (#745): подмешать разговоры лида, из которого выросла
   * сделка. Только список: в семантический поиск не уходит
   * (`recordsFilterParams` его не кладёт), там — одна сущность.
   */
  includeSourceLead?: boolean;
  callCategory?: string;
  callDirection?: 'inbound' | 'outbound' | 'unknown';
  agentId?: string;
  excludeMissed?: boolean;
  /** Hide the calls the product marks short (#876) — server-side, so the total agrees. */
  excludeShort?: boolean;
  search?: string;
  createdAfter?: string;
  createdBefore?: string;
  agentRating?: number;
  agentRatingMode?: 'gte' | 'eq' | 'lte';
  qaScoreMin?: number;
  qaScoreMax?: number;
  sentiment?: 'positive' | 'neutral' | 'negative';
  /** Only the caller's own records (#1235) — the list behind «my score». */
  ownRecords?: boolean;
  /** Условия по критериям (#865), «И». */
  criteria?: RecordCriterionCondition[];
  /** Шаг скрипта, не выполненный в звонке (#879), `<stepId>:missed` — как в адресе. */
  scriptStep?: string;
}

/**
 * The filters on the wire, keyed by the names the server's schema accepts: a
 * key it does not know fails to compile here instead of turning every
 * semantic search into a 400 (the server's filter schema is strict).
 */
export type RecordsFilterParams = {
  // `criterion` repeats (#865) — a list on the wire, every other filter one value.
  [Name in SemanticSearchFilterName]?: Name extends 'criterion' ? string[] : string;
};

/** Everything but paging and the text search, which each caller adds itself. */
export function recordsFilterParams(
  filters: RecordsServerFilters | undefined,
  context: { includeDemo?: 'yes' | 'no'; seriesId?: string }
): RecordsFilterParams {
  const params: RecordsFilterParams = {};
  if (context.includeDemo) params.includeDemo = context.includeDemo;
  if (filters?.projectIds && filters.projectIds.length > 0) {
    params.projectIds = filters.projectIds.join(',');
  } else if (filters?.projectId) {
    params.projectId = filters.projectId;
  }
  if (filters?.status) params.status = filters.status;
  if (filters?.outcome) params.outcome = filters.outcome;
  if (filters?.type) params.type = filters.type;
  if (filters?.crmEntityType) params.crmEntityType = filters.crmEntityType;
  if (filters?.crmEntityId) params.crmEntityId = filters.crmEntityId;
  // `includeSourceLead` — нет: эти параметры уходят и в семантический поиск,
  // а он лиды сделки не подмешивает. Список добавляет его сам (`useRecordsList`).
  if (filters?.callCategory) params.callCategory = filters.callCategory;
  if (filters?.callDirection) params.callDirection = filters.callDirection;
  if (filters?.agentId) params.agentId = filters.agentId;
  if (context.seriesId) params.seriesId = context.seriesId;
  if (filters?.excludeMissed) params.excludeMissed = 'true';
  if (filters?.excludeShort) params.excludeShort = 'true';
  if (filters?.ownRecords) params.ownRecords = 'true';
  if (filters?.qaScoreMin != null) params.qaScoreMin = String(filters.qaScoreMin);
  if (filters?.qaScoreMax != null) params.qaScoreMax = String(filters.qaScoreMax);
  if (filters?.sentiment) params.sentiment = filters.sentiment;
  if (filters?.criteria && filters.criteria.length > 0) {
    params.criterion = filters.criteria.map(formatRecordCriterionCondition);
  }
  if (filters?.scriptStep) params.scriptStep = filters.scriptStep;
  if (filters?.createdAfter) params.createdAfter = filters.createdAfter;
  if (filters?.createdBefore) params.createdBefore = filters.createdBefore;
  if (filters?.agentRating) {
    params.agentRating = String(filters.agentRating);
    if (filters.agentRatingMode) params.agentRatingMode = filters.agentRatingMode;
  }
  return params;
}
